<script setup lang="ts">
import { onMounted, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast } from "@/composables";
import {
  extractErrorMessage,
  fetchFormCodes,
  formatSlugLabel,
  saveFormCode,
  type FormCodeEntry,
} from "./obrsFormCodesApi";

const { successToast, errorToast } = useToast();

const loading = ref(false);
const modalOpen = ref(false);
const formCodes = ref<FormCodeEntry[]>([]);
const form = ref({
  subType: "",
  formCode: "",
});

const fetch = async () => {
  loading.value = true;
  try {
    formCodes.value = await fetchFormCodes();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to fetch form codes"));
  } finally {
    loading.value = false;
  }
};

const openEdit = (item: FormCodeEntry) => {
  form.value = {
    subType: item.subType,
    formCode: item.formCode,
  };
  modalOpen.value = true;
};

const save = async () => {
  loading.value = true;
  try {
    const response = await saveFormCode(form.value);
    successToast("Success", (response.data as { message?: string })?.message || "Form code updated successfully");
    modalOpen.value = false;
    await fetch();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Failed to update form code"));
  } finally {
    loading.value = false;
  }
};

onMounted(() => fetch());

defineExpose({ refresh: fetch });
</script>

<template>
  <div class="flex-1 flex flex-col">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-base font-semibold text-gray-800">Form Codes</h2>
        <p class="text-sm text-gray-500 mt-1">Manage eRegistry form codes by business sub type.</p>
      </div>
      <button type="button" class="btn-primary flex items-center gap-2" :disabled="loading" @click="fetch">
        <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div class="bg-white rounded border border-gray-200 overflow-hidden relative min-h-[300px]">
      <div
        v-if="loading && formCodes.length === 0"
        class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full text-xs">
          <thead class="bg-gray-50">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Sub Type</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Form Code</th>
            <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-24">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, idx) in formCodes" :key="item.subType" class="hover:bg-gray-50 transition">
            <td class="py-3 px-4 border-b border-gray-100 font-semibold">{{ idx + 1 }}</td>
            <td class="py-3 px-4 border-b border-gray-100">
              <span class="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary">
                {{ formatSlugLabel(item.subType) }}
              </span>
            </td>
            <td class="py-3 px-4 border-b border-gray-100 font-mono text-gray-600">{{ item.formCode }}</td>
            <td class="py-3 px-4 border-b border-gray-100 text-center">
              <button type="button" class="btn-sm-primary" title="Update Form Code" @click="openEdit(item)">
                <i class="fa-solid fa-pen"></i>
              </button>
            </td>
          </tr>
          <tr v-if="!loading && formCodes.length === 0">
            <td colspan="4" class="py-10 text-center text-gray-400">No form codes found.</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppModal v-model="modalOpen" xl2 hide-footer>
      <div class="p-4">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Update Form Code</h2>
        <form class="space-y-4" @submit.prevent="save">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Sub Type</label>
            <input
              v-model="form.subType"
              type="text"
              readonly
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-100 cursor-not-allowed"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Form Code</label>
            <input
              v-model="form.formCode"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary uppercase"
              placeholder="e.g. NF-9999"
            />
          </div>
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" class="btn-danger" @click="modalOpen = false">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="loading">
              {{ loading ? "Updating..." : "Update" }}
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  </div>
</template>
